import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get("plan");
  const next = params.get("next") || "/app";
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      if (plan) { await startCheckout(plan); }
      else { navigate(next); }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const google = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + next)}`;
  };

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border/60 bg-card/30 relative">
        <div className="absolute inset-0 grain opacity-50" />
        <Link to="/" className="relative"><Logo /></Link>
        <div className="relative">
          <h2 className="font-display text-3xl font-semibold tracking-tight leading-snug">Signup to first approved campaign in under 30 minutes.</h2>
          <p className="mt-4 text-muted-foreground max-w-sm">Describe your offer. The Copilot builds your ICP, sequence, and first-touch drafts for your approval.</p>
        </div>
        <p className="relative text-xs text-muted-foreground font-mono">No free plan · card required · cancel anytime</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Create your workspace</h1>
          <p className="text-sm text-muted-foreground mt-1">{plan ? "You'll continue to secure checkout." : "Start running compliant outbound."}</p>

          <button onClick={google} data-testid="google-signup-btn" className="mt-6 w-full h-10 rounded-md border border-border/70 flex items-center justify-center gap-2 text-sm hover:border-border transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> Continue with Google
          </button>
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><div className="flex-1 h-px bg-border" />or<div className="flex-1 h-px bg-border" /></div>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5" placeholder="Jane Doe" />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input id="company" data-testid="register-company" value={form.company} onChange={set("company")} required className="mt-1.5" placeholder="Acme" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} className="mt-1.5" placeholder="Min 6 characters" />
            </div>
            {error && <p className="text-sm text-rose-500" data-testid="register-error">{error}</p>}
            <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full rounded-md bg-amber-500 text-black hover:bg-amber-400">
              {loading ? "Creating…" : plan ? "Create & continue to checkout" : "Create workspace"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground text-center">Have an account? <Link to="/login" className="text-amber-500 hover:underline">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
